import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { useOrganizationSettingsQuery } from '@cast/infrastructure-abilities';
import {
  buildEnterpriseUrl,
  buildManagementUrl,
  useNavigate,
  useSetCurrentOrganizationId,
} from '@cast/infrastructure-organization';
import { useAbility } from '@cast/shared-ability-data-access';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { OrganizationType } from '@cast/types';

import { useIsOpsPilotFeatureFlagEnabled } from '../components/ai-assistant/useIsOpsPilotFeatureFlagEnabled';
import { useCallback } from 'react';

const CHATBOT_AVAILABLE_UNTIL = '2025-12-10';

dayjs.extend(utc);

export const useIsOpsPilotEnabled = (): {
  opsPilotStatus: 'disabled' | 'on' | 'off';
  onTryOpsPilot: () => void;
  tryOpsPilotDisabledTooltip: string | undefined;
} => {
  const navigate = useNavigate();

  const { currentOrganization } = useOrganizations();
  const ability = useAbility();
  const setCurrentOrganizationId = useSetCurrentOrganizationId();
  const organizationId = currentOrganization?.id;

  const isOpsPilotFFEnabled = useIsOpsPilotFeatureFlagEnabled();
  const canViewFeatureControl = ability.can(
    'view',
    'FeatureOrganizationManagementFeatureControl'
  );

  const parentId = currentOrganization?.parentId;
  const isEnterpriseChildOrg =
    currentOrganization?.type === OrganizationType.CHILD;
  const canViewEnterpriseManagement = ability.can(
    'view',
    'EnterpriseManagement'
  );
  const tryOpsPilotDisabledTooltip =
    isEnterpriseChildOrg && !canViewEnterpriseManagement
      ? 'Reach your Enterprise Admin to enable this feature.'
      : undefined;

  const { data: organizationSettings } = useOrganizationSettingsQuery({
    organizationId: organizationId,
    enabled: !!organizationId,
  });

  const onTryOpsPilot = useCallback(() => {
    if (parentId) {
      if (!canViewEnterpriseManagement) {
        return;
      }

      setCurrentOrganizationId(parentId);
      return navigate(buildEnterpriseUrl(parentId, 'feature-control'));
    }

    return navigate(buildManagementUrl('feature-control'));
  }, [
    parentId,
    navigate,
    canViewEnterpriseManagement,
    setCurrentOrganizationId,
  ]);

  if (!isOpsPilotFFEnabled || !canViewFeatureControl)
    return {
      opsPilotStatus: 'disabled',
      onTryOpsPilot,
      tryOpsPilotDisabledTooltip,
    };

  const copilotSettings = organizationSettings?.copilotSettings;
  const isOrganizationInitialized = copilotSettings?.organizationInitialized;
  const isEnabled = copilotSettings?.enabled;

  if (isOrganizationInitialized)
    return {
      opsPilotStatus: isEnabled ? 'on' : 'off',
      onTryOpsPilot,
      tryOpsPilotDisabledTooltip,
    };

  const isOldOrganization = currentOrganization?.createdAt
    ? dayjs
        .utc(currentOrganization.createdAt)
        .isBefore(dayjs.utc(CHATBOT_AVAILABLE_UNTIL))
    : false;

  return {
    opsPilotStatus: isOldOrganization ? 'on' : 'off',
    onTryOpsPilot,
    tryOpsPilotDisabledTooltip,
  };
};
